import type { ReactNode } from "react";
import { Icon } from "./icon/Icon";
import { Alert, Button, Spinner } from "./ui";
import { errorMessage } from "@/lib/api/client";

/** Loading / error / empty — the three states every panel needs. */

export function LoadingState({ label = "불러오는 중이에요" }: { label?: string }) {
  return (
    // <output> is an implicit live region, so the label is announced without
    // faking role="status" on a <div>.
    <output className="flex flex-col items-center justify-center gap-3 py-16">
      <Spinner size={28} />
      <p className="text-label-2 text-label-alternative">{label}</p>
    </output>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Alert
      tone="negative"
      title="데이터를 불러오지 못했어요"
      action={
        onRetry ? (
          <Button variant="text" size="small" onClick={onRetry}>
            다시 시도
          </Button>
        ) : undefined
      }
    >
      {errorMessage(error)}
    </Alert>
  );
}

export function EmptyState({
  title = "표시할 데이터가 없어요",
  description = "기간을 넓히거나 필터를 지워 보세요.",
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <Icon name="search" size={28} style={{ color: "var(--label-assistive)" }} />
      <p className="text-body-2 font-semibold text-label-normal">{title}</p>
      <p className="text-label-2 text-label-alternative">{description}</p>
      {action}
    </div>
  );
}

/** One wrapper so every panel handles the three states the same way. */
export function QueryPanel<T>({
  query,
  isEmpty,
  empty,
  children,
}: {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => void };
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={query.refetch} />;
  if (query.data === undefined) return <EmptyState />;
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState />}</>;
  return <>{children(query.data)}</>;
}
